import { useState } from 'react';
import {
  Alert,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  TextField,
} from '@mui/material';

import Button from '@/components/Button';
import {
  experimentsAPI,
  extractExperimentsApiError,
  FeatureFlagDetail,
} from '@/services/experimentsAPI';

export type Props = {
  onClose: () => void;
  onCreated: (flag: FeatureFlagDetail) => void;
};

const CreateFlagModal = ({ onClose, onCreated }: Props) => {
  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultEnabled, setDefaultEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // graphy validates keys only for presence/uniqueness, and every member/
  // nested route embeds the key in the path — a key with "/", "?", "#", "%"
  // or whitespace creates fine (the key travels in the POST body) and is then
  // permanently unmanageable. "me"/"results" shadow the collection routes.
  const trimmedKey = key.trim();
  const KEY_FORMAT = /^[A-Za-z0-9][A-Za-z0-9._-]*$/;
  const RESERVED_KEYS = ['me', 'results'];
  const keyFormatValid =
    trimmedKey.length === 0 ||
    (KEY_FORMAT.test(trimmedKey) && !RESERVED_KEYS.includes(trimmedKey));

  const canSave =
    trimmedKey.length > 0 &&
    keyFormatValid &&
    name.trim().length > 0 &&
    !saving;

  const handleSubmit = async () => {
    if (!canSave) return;
    setSaving(true);
    setError(null);
    try {
      const flag = await experimentsAPI.createFlag({
        key: key.trim(),
        name: name.trim(),
        // Send '' (not undefined) — see ExperimentModal's identical fix;
        // `x: undefined` is dropped by JSON.stringify, so an emptied
        // description would silently fail to clear.
        description: description.trim(),
        default_enabled: defaultEnabled,
      });
      onCreated(flag);
      onClose();
    } catch (err: any) {
      setError(
        extractExperimentsApiError(err, 'Failed to create feature flag'),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New feature flag</DialogTitle>
      <DialogContent
        sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
      >
        {error && <Alert severity="error">{error}</Alert>}
        <TextField
          autoFocus
          label="Key"
          error={!keyFormatValid}
          helperText={
            keyFormatValid
              ? 'Immutable identifier read by the app, e.g. v2.2507.17_preferred-staffing-modal'
              : 'Letters, digits, dots, dashes and underscores only (and not "me"/"results") — other characters make the flag unmanageable.'
          }
          placeholder="e.g. v2.2507.17_preferred-staffing-modal"
          value={key}
          onChange={(e) => setKey(e.target.value)}
          fullWidth
          size="small"
          sx={{ mt: 1 }}
        />
        <TextField
          label="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          fullWidth
          size="small"
        />
        <TextField
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
          multiline
          minRows={2}
          size="small"
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={defaultEnabled}
              onChange={(e) => setDefaultEnabled(e.target.checked)}
            />
          }
          label={
            'Default enabled (state when no experiment is active). Note: this resolves the flag to the variant "on" — a consumer gating on "treatment" (like the preferred-staffing modal) stays OFF; use an experiment to roll such features out.'
          }
        />
      </DialogContent>
      <DialogActions>
        <Button
          a11yLabel="Cancel"
          label="Cancel"
          appearance="plain"
          onClick={onClose}
        />
        <Button
          a11yLabel="Create feature flag"
          label="Create"
          isLoading={saving}
          disabled={!canSave}
          onClick={handleSubmit}
        />
      </DialogActions>
    </Dialog>
  );
};

export default CreateFlagModal;
